import { useId, useState } from 'react';

import type { SalespeopleStatus } from '@/shared/notices/types';
import { useSalespeopleStatus } from '@/shared/notices/useNotices';
import type { Finding, Version } from '@/shared/tariffs/types';
import { useConfirmColumns, usePublish } from '@/shared/tariffs/useTariffs';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { Stat } from '@/shared/ui/Stat';

const COUNT = new Intl.NumberFormat('es-ES');
const CHANGE = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 1,
  signDisplay: 'exceptZero',
});

/** Today in Madrid as the date input wants it, `2026-10-15`. */
function todayInput(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return parts;
}

function FindingCallout({ finding }: { finding: Finding }) {
  const examples = finding.examples ?? [];
  return (
    <Callout tone={finding.blocking ? 'danger' : 'warn'} compact title={finding.message}>
      {examples.length > 0 ? (
        <span className="text-[13px]">
          Por ejemplo: {examples.join(', ')}
          {(finding.count ?? 0) > examples.length ? '…' : '.'}
        </span>
      ) : null}
    </Callout>
  );
}

/**
 * A version read and not published yet: what it brings against the one in force, what
 * the checks found, and «Publicar». Only one thing stops a tariff (the price missing in
 * most rows, `blocking`); the rest warn, and whoever publishes decides (ADR 0043 of the
 * back). Publishing is refused if the version in force changed since this one was read:
 * reading it again compares it with the new one.
 */
export function DraftReview({
  version,
  onChangeColumns,
}: {
  version: Version;
  onChangeColumns?: () => void;
}) {
  const publish = usePublish(version.id);
  const reread = useConfirmColumns(version.id);
  const [date, setDate] = useState(() => todayInput());
  const [notify, setNotify] = useState(true);
  const [email, setEmail] = useState(false);
  const [mailbox, setMailbox] = useState('');
  const salespeople = useSalespeopleStatus(Boolean(version.can_publish) && notify);
  const canEmail = Boolean(salespeople.data) && !salespeople.data?.blocked_reason;
  const sending = notify && email && canEmail;
  // A choice the back no longer offers (a refetch after the mailbox was taken away) is no
  // choice: it would be refused, with the select already hidden.
  const offered = (salespeople.data?.mailboxes ?? []).some((each) => each.mailbox === mailbox);
  const from = (offered ? mailbox : '') || salespeople.data?.mailbox || '';
  const mustChoose = sending && (salespeople.data?.mailboxes?.length ?? 0) > 1 && !from;
  const summary = version.summary;
  const findings = version.findings ?? [];
  const blocking = findings.filter((each) => each.blocking);
  const warnings = findings.filter((each) => !each.blocking);
  const columns = version.columns;
  const canReread = Boolean(columns?.sheet && version.mapping);

  return (
    <Card title="Antes de publicar">
      <div className="flex flex-col gap-4">
        {summary ? (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                label="Artículos"
                value={COUNT.format(summary.items ?? 0)}
                hint={`${COUNT.format(summary.with_price ?? 0)} con precio`}
              />
              {summary.previous_items !== null && summary.previous_items !== undefined ? (
                <>
                  <Stat
                    label="Nuevos"
                    value={COUNT.format(summary.new ?? 0)}
                    hint={`${COUNT.format(summary.gone ?? 0)} ya no están`}
                  />
                  <Stat
                    label="Cambian de precio"
                    value={COUNT.format(summary.changed ?? 0)}
                    hint={`${COUNT.format(summary.up ?? 0)} suben, ${COUNT.format(summary.down ?? 0)} bajan`}
                  />
                  <Stat
                    label="Cambio medio"
                    value={
                      summary.average_change_pct
                        ? `${CHANGE.format(Number(summary.average_change_pct))} %`
                        : '—'
                    }
                    hint={`frente a ${COUNT.format(summary.previous_items)} de la vigente`}
                  />
                </>
              ) : null}
            </div>
            {summary.previous_items === null || summary.previous_items === undefined ? (
              <p className="text-ink-3 text-[13.5px]">
                Es la primera versión de esta tarifa: no hay con qué compararla.
              </p>
            ) : null}
            {(summary.new_examples ?? []).length > 0 || (summary.gone_examples ?? []).length > 0 ? (
              <div className="text-ink-2 grid gap-1 text-[13px] sm:grid-cols-2">
                {(summary.new_examples ?? []).length > 0 ? (
                  <p>
                    <span className="text-ink-3">Nuevos: </span>
                    {(summary.new_examples ?? []).join(', ')}
                  </p>
                ) : null}
                {(summary.gone_examples ?? []).length > 0 ? (
                  <p>
                    <span className="text-ink-3">Ya no están: </span>
                    {(summary.gone_examples ?? []).join(', ')}
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}

        {blocking.map((finding) => (
          <FindingCallout key={finding.code} finding={finding} />
        ))}
        {warnings.map((finding) => (
          <FindingCallout key={finding.code} finding={finding} />
        ))}
        {findings.length === 0 ? (
          <p className="text-ink-2 text-[13.5px]">
            Las comprobaciones no han encontrado nada raro.
          </p>
        ) : null}

        {version.can_publish ? (
          <PublishNotice
            notify={notify}
            onNotify={setNotify}
            email={email}
            onEmail={setEmail}
            mailbox={from}
            onMailbox={setMailbox}
            status={salespeople.data}
            loading={salespeople.isPending && salespeople.fetchStatus !== 'idle'}
            failed={salespeople.isError}
            supplier={version.supplier_name}
          />
        ) : null}
        <div className="flex flex-wrap items-end gap-3">
          <Input
            type="date"
            label="Fecha de la tarifa"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            hint="La que pone el proveedor; hoy si no pone ninguna."
            className="w-44"
          />
          <Button
            disabled={!version.can_publish || publish.isPending || mustChoose}
            onClick={() =>
              publish.mutate({
                tariff_date: date || null,
                notify_team: notify,
                email_salespeople: sending,
                mailbox: sending ? from || null : null,
              })
            }
          >
            {publish.isPending ? 'Publicando…' : 'Publicar'}
          </Button>
          {onChangeColumns && columns ? (
            <Button variant="secondary" onClick={onChangeColumns}>
              Cambiar las columnas
            </Button>
          ) : null}
        </div>
        <p className="text-ink-3 text-[13px]">
          {mustChoose
            ? 'Elige desde qué buzón sale el correo a Comerciales para publicarla.'
            : version.can_publish
              ? 'Al publicarla, el buscador usa esta versión y la que estaba vigente pasa al historial.'
              : 'No se puede publicar así: cambia las columnas o descártala.'}
        </p>
        {publish.isError ? (
          <ErrorMessage error={publish.error}>No se pudo publicar</ErrorMessage>
        ) : null}
        {/* Only one refusal is fixed by reading again: the tariff in force changed since
            this one was read (`tariff_changed`). The others (somebody else published or
            discarded it) bring the version back as it is now. */}
        {publish.isError && publish.error.reason === 'tariff_changed' && canReread ? (
          <div>
            <Button
              variant="secondary"
              size="sm"
              disabled={reread.isPending}
              onClick={() =>
                reread.mutate({
                  sheet: columns?.sheet ?? '',
                  header_row: columns?.header_row ?? 0,
                  mapping: version.mapping ?? {},
                })
              }
            >
              {reread.isPending ? 'Pidiendo la lectura…' : 'Volver a leerla'}
            </Button>
          </div>
        ) : null}
        {reread.isError ? (
          <ErrorMessage error={reread.error}>No se pudo pedir la lectura</ErrorMessage>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * What else «Publicar» does (contract v10.50, ADR 0045 of the back): the notice to the
 * team, on by default, and the same notice by email to «Comerciales», in blind copy, with
 * the link and never the Excel. The back checks the email before publishing and refuses
 * the whole thing if it cannot go (409 `salespeople_email`); here the box says beforehand
 * why it cannot, so nobody ticks it for nothing.
 */
function PublishNotice({
  notify,
  onNotify,
  email,
  onEmail,
  mailbox,
  onMailbox,
  status,
  loading,
  failed,
  supplier,
}: {
  notify: boolean;
  onNotify: (value: boolean) => void;
  email: boolean;
  onEmail: (value: boolean) => void;
  mailbox: string;
  onMailbox: (value: string) => void;
  status: SalespeopleStatus | undefined;
  loading: boolean;
  failed: boolean;
  supplier: string | null | undefined;
}) {
  const id = useId();
  const blocked = status?.blocked_reason ?? null;
  const members = status?.members ?? 0;
  const options = status?.mailboxes ?? [];
  const canEmail = Boolean(status) && !blocked;
  // The explanation goes in the description of each box, not in its name (`Input.tsx`).
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="sr-only">Al publicar</legend>
      <div className="text-ink-2 flex items-start gap-2 text-[13.5px]">
        <input
          id={`${id}-notify`}
          type="checkbox"
          className="accent-accent mt-0.5 size-4"
          checked={notify}
          aria-describedby={`${id}-notify-hint`}
          onChange={(event) => onNotify(event.target.checked)}
        />
        <span className="flex flex-col">
          <label htmlFor={`${id}-notify`} className="text-ink font-medium">
            Avisar al equipo
          </label>
          <span id={`${id}-notify-hint`} className="text-ink-3 text-[12.5px]">
            Todos verán al entrar «Tarifa nueva de {supplier || 'este proveedor'}» con lo que trae.
            Quítalo para una tarifa que solo se carga para tenerla.
          </span>
        </span>
      </div>
      {notify ? (
        <div className="text-ink-2 flex items-start gap-2 text-[13.5px]">
          <input
            id={`${id}-email`}
            type="checkbox"
            className="accent-accent mt-0.5 size-4"
            checked={email && canEmail}
            disabled={!canEmail}
            aria-describedby={`${id}-email-hint`}
            onChange={(event) => onEmail(event.target.checked)}
          />
          <span className="flex flex-col">
            <label
              htmlFor={`${id}-email`}
              className={canEmail ? 'text-ink font-medium' : 'text-ink-3 font-medium'}
            >
              Mandarlo también por correo a Comerciales
              {members > 0 ? ` (${members})` : ''}
            </label>
            <span id={`${id}-email-hint`} className="text-ink-3 text-[12.5px]">
              {loading
                ? 'Comprobando si puede salir…'
                : blocked
                  ? blocked
                  : failed || !status
                    ? 'No se pudo comprobar si puede salir. Publica sin el correo o vuelve a intentarlo en un momento.'
                    : 'En copia oculta, con el enlace al proveedor y sin el Excel.'}
            </span>
          </span>
        </div>
      ) : null}
      {notify && email && canEmail && options.length > 1 ? (
        <Select
          label="Desde"
          value={mailbox}
          onChange={(event) => onMailbox(event.target.value)}
          className="w-72 max-w-full"
          hint="El buzón desde el que sale el correo."
        >
          <option value="">Elige el buzón</option>
          {options.map((each) => (
            <option key={each.mailbox} value={each.mailbox}>
              {each.from_address}
            </option>
          ))}
        </Select>
      ) : null}
    </fieldset>
  );
}
